import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import { Video } from '../../../types/videos';

export function useVideosList() {
  const [items, setItems] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: Video[]; message?: string }>("/videos");
      // Our ApiClient returns the parsed JSON body directly.
      // Backend responds with { data, message }, so use res.data if present; otherwise fallback.
      const list = (res as any)?.data ?? (Array.isArray(res) ? res : []);
      setItems(list);
    } catch (e: any) {
      setError(e.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(x => x.filename.toLowerCase().includes(q));
  }, [items, query]);

  const total = filtered.length;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageData = filtered.slice(start, end);

  const remove = async (id: string) => {
    await api.delete(`/videos/${id}`);
    await fetchAll();
  };

  return {
    items: pageData,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    query,
    setQuery,
    loading,
    error,
    refresh: fetchAll,
    remove,
  };
}
